// node_modules
import { FC, useCallback, useContext } from "react";
// Contexts
import { CaseContext, TechnologyStore } from "Stores";
// Components
import { ConceptTechnologyHeader } from "Components/Case/Results/ConceptTechnologyHeader";
import { ConceptTechnologyFooter } from "Components/Case/Results/ConceptTechnologyFooter";
import { DeliveryOverview } from "Components/Case/Results/DeliveryOverview";
import { SelectedTechnology } from "Components/Case/Results/SelectedTechnology";
import { TechnologyOrConceptDescription } from "Components/Case/Results/TechnologyOrConceptDescription";
import { TiPartialOverview } from "../DeliveryOverview/TiOverview";
// Enums
import { CaseActionTypeEnum, CasePageEnum } from "Enums";
// Constants
import { ConceptConstants } from "Constants";
// styles
import styles from "./resultsMainContent.module.scss";
import { useHash } from "Hooks";
import { ICaseSelectedTechnology } from "Interfaces";
import { TTechnology } from "Types";
import { ReferenceHelperSingleton } from "Helpers";

export const ResultsMainContent: FC = () => {
    // Contexts
    const { caseState, caseDispatch } = useContext(CaseContext);

    // Logic
    const hashChangeHandler = useCallback((): void => {
        const hashSplit: string[] = window.location.hash.split("#gototechnology-");

        ReferenceHelperSingleton.removeHashFromURL();
        
        if (hashSplit.length < 0 || 
                hashSplit.length > 2 ||
                isNaN(parseInt(hashSplit[1], 10)) ||
                !caseState.conceptTechnologyList ||
                !caseState.selectedCaseId)  { return; }
        
        const technologyId: number = parseInt(hashSplit[1], 10);
        let relatedTechnology: TTechnology | undefined = undefined;

        for (const concept of caseState.conceptTechnologyList.concepts) {
            for (const technology of concept.technologies) {
                if (technology.originalTechnologyId && technologyId === technology.originalTechnologyId) {
                    relatedTechnology = technology;
                    break;
                }
            }
        }
        if (!relatedTechnology && caseState.conceptTechnologyList.additionalTechnologies) {
            for (const technology of caseState.conceptTechnologyList.additionalTechnologies) {
                if (technologyId === technology.id) {
                    relatedTechnology = technology;
                    break;
                }
            }
        }

        if (relatedTechnology) {
            caseDispatch({
                type: CaseActionTypeEnum.SetCaseSelectedTechnology,
                selectedTechnologyId: relatedTechnology.id,
                selectedOriginalTechnologyId: relatedTechnology.originalTechnologyId,
                selectedEntityName: relatedTechnology.name,
                selectedEntityNumbering: relatedTechnology.numbering
            } as ICaseSelectedTechnology);
        }
    }, [caseDispatch, caseState.conceptTechnologyList, caseState.selectedCaseId]);
    
    // Custom hooks
    useHash(hashChangeHandler);

    // Logic
    const getResultsContainerClassName = (): string => {
        const classNameList = [styles.resultsMainContentContainer];
        return classNameList.join(" ");
    };

    if (caseState.selectedTechnologyId) {
        return (
            <>
                <div className={getResultsContainerClassName()}>
                    <TechnologyStore>
                        <SelectedTechnology />
                    </TechnologyStore>
                </div>
            </>
        );
    } else if (caseState.selectedConceptId &&
        caseState.selectedConceptId === ConceptConstants.FIXED_ADDITIONAL_CONCEPT_ID) {
        return (
            <>
                <div className={getResultsContainerClassName()}>
                    <div className={styles.additionalResultsHeader}>
                        <h2>{ConceptConstants.FIXED_ADDITIONAL_CONCEPT_NAME}</h2>
                    </div>
                    <div className={[styles.conceptDescriptionContainer, styles.scrollbarCustomThin].join(" ")}>
                        <div style={{ marginBottom: "2rem" }}>
                            <h5>Description</h5>
                            <div>{ConceptConstants.FIXED_ADDITIONAL_CONCEPT_DESCRIPTION}</div>
                        </div>
                    </div>
                    <ConceptTechnologyFooter />
                </div>
            </>
        );
    } else if (caseState.selectedConceptId) {
        return (
            <>
                <div className={getResultsContainerClassName()}>
                    <ConceptTechnologyHeader />
                    <div className={[styles.conceptDescriptionContainer, styles.scrollbarCustomThin].join(" ")}>
                        <TechnologyOrConceptDescription typeId={caseState.selectedConceptId} typeName="concepts" />
                    </div>
                    <ConceptTechnologyFooter />
                </div>
            </>
        );
    } else if (caseState.selectedEntityId) {
        if (caseState.selectedEntityName?.toLocaleLowerCase() === CasePageEnum.Overview) {
            return (
                <>
                    <div className={getResultsContainerClassName()}>
                        <DeliveryOverview />
                    </div>
                </>
            );
        } else if (caseState.selectedEntityName?.toLocaleLowerCase() === CasePageEnum.RequiremenstTable) {
            return (
                <>
                    <div className={getResultsContainerClassName()} style={{ overflow: "hidden" }}>
                        <TiPartialOverview casePage={CasePageEnum.RequiremenstTable} />
                    </div>
                </>
            );
        } else if (caseState.selectedEntityName?.toLocaleLowerCase() === CasePageEnum.Radar) {
            return (
                <>
                    <div className={getResultsContainerClassName()}>
                        <TiPartialOverview casePage={CasePageEnum.Radar} />
                    </div>
                </>
            );
        } else {
            return (<></>);
        }
    } else {
        return (<></>);
    }
};